import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useContent } from '../contexts/ContentContext';
import { pages, pageTitle, resolvePage } from '../pages';
import SiteMap from './SiteMap';
import ThemeSwitcher from './ThemeSwitcher';
import LanguageSwitcher from './LanguageSwitcher';
import { Image, Music, FileText, Twitch } from 'lucide-react';
import { useBrandDesign } from '../contexts/BrandDesignContext';

const navigationIcons = { gallery: Image, works: Music, article: FileText, twitch_guide: Twitch };

const Header: React.FC = () => {
    const { content, language, t } = useContent();
    const { variant } = useBrandDesign();
    const route = resolvePage(window.location.search);
    const header = useRef<HTMLElement>(null);
    const brand = useRef<HTMLAnchorElement>(null);
    const navigation = useRef<HTMLElement>(null);
    const actions = useRef<HTMLDivElement>(null);
    const [compact, setCompact] = useState(false);
    useLayoutEffect(() => {
        const update = () => {
            if (!header.current || !navigation.current || !actions.current || !brand.current) return;
            const style = getComputedStyle(header.current);
            const navStyle = getComputedStyle(navigation.current);
            const actionStyle = getComputedStyle(actions.current);
            const links = Array.from(navigation.current.children);
            const controls = Array.from(actions.current.children);
            const required = brand.current.scrollWidth
                + links.reduce((sum, link) => sum + link.getBoundingClientRect().width, 0)
                + parseFloat(navStyle.columnGap || '0') * Math.max(0, links.length - 1) + 6
                + controls.reduce((sum, control) => sum + control.getBoundingClientRect().width, 0)
                + parseFloat(actionStyle.columnGap || '0') * Math.max(0, controls.length - 1)
                + parseFloat(style.columnGap || '0') * 2;
            const available = header.current.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
            setCompact(required > available + 1);
        };
        const observer = new ResizeObserver(update);
        [header.current, brand.current, navigation.current, actions.current, ...Array.from(navigation.current?.children || [])].forEach(element => { if (element) observer.observe(element); });
        window.addEventListener('resize', update);
        update();
        return () => { observer.disconnect(); window.removeEventListener('resize', update); };
    }, [language, route.definition.kind]);
    useEffect(() => {
        const update = () => document.documentElement.style.setProperty('--lab-header-height', `${header.current?.offsetHeight || 64}px`);
        const observer = new ResizeObserver(update);
        if (header.current) observer.observe(header.current);
        update();
        return () => observer.disconnect();
    }, []);
    return <header ref={header} className="lab-header" data-nav-overflow={compact || undefined} style={{ '--lab-brand-image': "url('/assets/landing/hero-bg.webp')" } as React.CSSProperties}>
        <a ref={brand} href="/" className={`lab-brand lab-brand--${variant}`} aria-hidden={compact || undefined} tabIndex={compact ? -1 : undefined} aria-label={content.ui[language].siteMap.topPage}>
            <img src="/favicon.png" alt="" width="24" height="24" />
            <svg className="lab-brand-wordmark" viewBox="0 0 172 44" role="img" aria-label={content.siteName} focusable="false">
                {variant === 'a' ? <>
                    <path className="lab-brand-speedline" d="M2 12h40M8 18h31M129 30h36" />
                    <path className="lab-brand-speedline lab-brand-speedline--blue" d="M136 36h25" />
                    <text className="lab-brand-wordmark-type" x="86" y="31" textAnchor="middle" textLength="164" lengthAdjust="spacingAndGlyphs">{content.siteName}</text>
                </> : <>
                    <path className="lab-brand-serif-bar" d="M2 31h168v5H2z" />
                    <text className="lab-brand-wordmark-echo" x="86" y="30" textAnchor="middle" textLength="164" lengthAdjust="spacingAndGlyphs">{content.siteName}</text>
                    <text className="lab-brand-wordmark-type" x="86" y="30" textAnchor="middle" textLength="164" lengthAdjust="spacingAndGlyphs">{content.siteName}</text>
                </>}
            </svg>
        </a>
        <nav ref={navigation} className="lab-primary-nav" aria-label={t('common.menu')}>
            {pages.filter(page => ['gallery', 'works', 'article', 'twitch_guide'].includes(page.id)).map(page => {
                const Icon = navigationIcons[page.id as keyof typeof navigationIcons];
                const label = pageTitle(page, content, language, t);
                return <a key={page.id} href={page.href} aria-label={label} title={label} aria-current={route.definition.id === page.id ? 'page' : undefined}><Icon aria-hidden="true" /><span>{label}</span></a>;
            })}
        </nav>
        <div ref={actions} className="lab-header-actions">
            <ThemeSwitcher />
            <div className="lab-header-language"><LanguageSwitcher /></div>
            <SiteMap />
        </div>
    </header>;
};
export default Header;
